import config from "@config";

interface Features {
  webSearchEnabled: boolean;
  isDemoMode: boolean;
  whatsapp: boolean;
  analyticsDashboard: boolean;
  sso: boolean;
}

export type WelcomeLanguage = "de" | "en" | "fr" | "it";

export interface AppConfig {
  name: string;
  themeColor: string;
  assistantName: string;
  assistantPhoto?: string;
  logo: string;
  welcomeMessage: string;
  welcomeMessages?: Partial<Record<WelcomeLanguage, string>>;
  features: Features;
}

type RuntimeOverrides = {
  assistantName?: string;
  welcomeMessageTemplate?: string;
  welcomeMessages?: Partial<Record<WelcomeLanguage, string>>;
  assistantPhoto?: string;
};

const RUNTIME_CONFIG_KEY = "runtime_app_config_overrides";

function getRuntimeOverrides(): RuntimeOverrides {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(RUNTIME_CONFIG_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return parsed;
  } catch {
    return {};
  }
}

export function saveRuntimeOverrides(overrides: RuntimeOverrides) {
  if (typeof window === "undefined") return;
  localStorage.setItem(RUNTIME_CONFIG_KEY, JSON.stringify(overrides));
}

function resolveRuntimeConfig(): Required<RuntimeOverrides> {
  const runtimeOverrides = getRuntimeOverrides();
  return {
    assistantName: runtimeOverrides.assistantName || config.assistantName,
    welcomeMessageTemplate:
      runtimeOverrides.welcomeMessageTemplate || config.welcomeMessage,
    welcomeMessages:
      runtimeOverrides.welcomeMessages || (config as any).welcomeMessages || {},
    assistantPhoto: runtimeOverrides.assistantPhoto || (config as any).assistantPhoto || "",
  };
}

export const appConfig: AppConfig = {
  name: config.name,
  themeColor: config.themeColor,
  get assistantName() {
    return resolveRuntimeConfig().assistantName;
  },
  get assistantPhoto() {
    return resolveRuntimeConfig().assistantPhoto;
  },
  logo: config.logo,
  get welcomeMessage() {
    const runtime = resolveRuntimeConfig();
    return runtime.welcomeMessageTemplate
      .split("{ASSISTANT_NAME}")
      .join(runtime.assistantName);
  },
  welcomeMessages: (config as any).welcomeMessages,
  features: {
    webSearchEnabled: config.features.webSearch,
    isDemoMode: config.features.isDemoMode,
    whatsapp: config.features.whatsapp,
    analyticsDashboard: config.features.analyticsDashboard ?? false,
    sso: config.features.sso ?? false,
  },
};

export function getWelcomeMessageTemplate(language: WelcomeLanguage = "de"): string {
  const runtime = resolveRuntimeConfig();
  const localizedTemplate = runtime.welcomeMessages?.[language];
  if (localizedTemplate) return localizedTemplate;

  return runtime.welcomeMessageTemplate;
}

export const APP_NAME = appConfig.name;

export const PLATFORM_BRANDING_LABEL = "powered by";
export const PLATFORM_BRANDING_LOGO = "/coorpix-logo.svg";
export const PLATFORM_BRANDING_URL = "https://www.coorpix.ch/";
